import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
// ✅ MIGRATED: Now using shared utilities
import { distributeTextCanvas } from '@/utils/coverImages/textDistribution';
import { replaceQuotes } from '@/utils/helpers';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { createWatermarkTagImage } from '../shared';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
  isAiGenerated?: boolean;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset, isAiGenerated }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    // ✅ REMOVED: setCrossOriginForExternalImages - now handled automatically by shared utility

    // ✅ REFACTORED: Using generateSingleImage from shared utility
    const generateImageVariations = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var33');

            const leftImage = new Image();
            leftImage.src = variation.leftImageSrc;
            leftImage.classList.add('left-image-var33');
            imageHolder.appendChild(leftImage);

            const rightImage = new Image();
            rightImage.src = variation.rightImageSrc;
            rightImage.classList.add('right-image-var33');
            imageHolder.appendChild(rightImage);

            const actualPhoto = createWatermarkTagImage(
              'actual-photo-var33',
              variation.leftWatermarkTag,
              variation.leftWatermarkPosition,
            );
            if (actualPhoto) {
              imageHolder.appendChild(actualPhoto);
            }

            const rightActualPhoto = createWatermarkTagImage(
              'actual-photo-var33',
              variation.rightWatermarkTag,
              variation.rightWatermarkPosition,
            );
            if (rightActualPhoto) {
              rightActualPhoto.classList.add('actual-photo-var33--right');
              imageHolder.appendChild(rightActualPhoto);
            }

            if (variation.rightText) {
              const textHolder = document.createElement('div');
              const paragraph = document.createElement('p');

              textHolder.classList.add('text-var33');

              if (variation.fullText) {
                textHolder.classList.add('text-var33--full');
              }
              if (variation.textPosition === 'bottom') {
                textHolder.classList.add('text-var33--bottom');
              }

              const updatedText = replaceQuotes(variation.rightText);

              let text = '';
              if (variation.quotes) {
                text = `“${updatedText}”`;
              } else {
                text = updatedText;
              }
              if (variation.brakes) {
                paragraph.innerText = text;
                paragraph.classList.add('custom-brakes');
              } else {
                if (variation.fullText) {
                  let fullFontSize = 55;
                  if (text.length > 49) {
                    textHolder.classList.add('text-var33--multiple-lines');
                    fullFontSize = 45;
                  }
                  paragraph.innerHTML = distributeTextCanvas(text, {
                    minFontSize: fullFontSize,
                    maxWidth: 1080,
                  });
                } else {
                  paragraph.innerHTML = distributeTextCanvas(text, {
                    minFontSize: 45,
                    maxWidth: 960,
                  });
                }
              }

              textHolder.append(paragraph);
              imageHolder.appendChild(textHolder);
            }

            if (variation.divider !== 'none') {
              const middleBreak = document.createElement('div');
              middleBreak.classList.add('break-var33');
              if (variation.divider === 'black') {
                middleBreak.classList.add('break-var33--black');
              }
              imageHolder.appendChild(middleBreak);
            }

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var33',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    // ✅ REFACTORED: Using generateImagesInParallel with built-in progress tracking
    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariations,
          (current) => {
            setCounter(current);
          },
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    // ✅ REFACTORED: Using shared downloadImagesAsZip utility
    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadImagesAsZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                  isAiGenerated={isAiGenerated}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
